import React, { createContext, useContext, useEffect, useState } from "react";
import { IFavoriteItem } from "./interfaces/FavoriteItem";

interface FavoriteListContextProps {
  favoriteList: IFavoriteItem[];
  setFavoriteList: React.Dispatch<React.SetStateAction<IFavoriteItem[]>>;
}

const FavoriteListContext = createContext<FavoriteListContextProps | undefined>(
  undefined,
);

const FavoriteListProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [favoriteList, setFavoriteListState] = useState<IFavoriteItem[]>(() => {
    const stored = localStorage.getItem("favoriteList");
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    const syncFromStorage = () => {
      const stored = localStorage.getItem("favoriteList");
      setFavoriteList(stored ? JSON.parse(stored) : []);
    };
    window.addEventListener("storage", syncFromStorage);
    return () => window.removeEventListener("storage", syncFromStorage);
  }, []);

  const setFavoriteList: React.Dispatch<
    React.SetStateAction<IFavoriteItem[]>
  > = (value) => {
    if (typeof value === "function") {
      setFavoriteListState((prev) =>
        (value as (prevState: IFavoriteItem[]) => IFavoriteItem[])(prev),
      );
    } else {
      setFavoriteListState(value);
    }
  };

  return (
    <FavoriteListContext.Provider value={{ favoriteList, setFavoriteList }}>
      {children}
    </FavoriteListContext.Provider>
  );
};

const useFavoriteListContext = (): FavoriteListContextProps => {
  const context = useContext(FavoriteListContext);
  if (!context) {
    throw new Error(
      "useFavoriteListContext must be used within a FavoriteListProvider",
    );
  }
  return context;
};

export { FavoriteListProvider, useFavoriteListContext };
